/**
 * AnswerColumnsSettings - the "Recruitment list" block on a study's Overview tab
 * (docs/plans/crm-recruitment-list-plan.md decision 7, B1, B3, B4; mounted for CRM admins by
 * pages/studies/StudyDetail.tsx).
 *
 * Up to six questions from the study's landing form, each with the short label the recruitment
 * list shows as its column header: one agreed set per study that every recruiter sees (each person
 * can still hide a column from the sheet's column chooser). It reads
 * GET /api/crm/recruit/studies/:studyId/columns (the chosen columns, and the landing form's
 * questions to choose from) and saves the whole set with the admin-only PUT. Anyone who is not a
 * CRM admin sees the set disabled with a hint; the server refuses their PUT regardless.
 *
 * A chosen question that is no longer on the landing form stays in the list, flagged, until an
 * admin removes it: nothing an admin chose is dropped without a word.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronRight, ClipboardCheck, Loader2, Save, Trash2 } from 'lucide-react';
import { Alert, Badge, btnGhost, btnPrimary, fieldCls, iconBtn } from '../components/ui';
import { MAX_ANSWER_COLUMNS } from './recruitLogic';
import { getRecruitColumns, isRecruitUnavailable, putRecruitColumns, recruitErrorMessage } from './api';
import type { RecruitAnswerColumn } from './types';

/** The longest column label: it is the header of a sheet column about 144 px wide. */
export const ANSWER_LABEL_MAX = 60;

const squash = (s: string): string => s.replace(/\s+/g, ' ').trim();

/**
 * A question's text as the column's first label: whole when it fits, else cut back to the last
 * word that fits (an admin shortens it further by hand; the header is narrow).
 */
export function defaultColumnLabel(text: string, max: number = ANSWER_LABEL_MAX): string {
  const t = squash(text || '');
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return (space >= Math.floor(max / 2) ? cut.slice(0, space) : cut).trim();
}

/** Each question once, keyed and labelled (a question without text shows its key). */
function cleanQuestions(list: readonly RecruitAnswerColumn[] | null | undefined): RecruitAnswerColumn[] {
  const out: RecruitAnswerColumn[] = [];
  const seen = new Set<string>();
  for (const q of list || []) {
    const key = typeof q?.question_key === 'string' ? q.question_key.trim() : '';
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const label = typeof q.label === 'string' ? squash(q.label) : '';
    out.push({ question_key: key, label: label || key });
  }
  return out;
}

/** The chosen columns as the server holds them: each question once, at most six, in order. */
function cleanColumns(list: readonly RecruitAnswerColumn[] | null | undefined): RecruitAnswerColumn[] {
  const out: RecruitAnswerColumn[] = [];
  const seen = new Set<string>();
  for (const c of list || []) {
    const key = typeof c?.question_key === 'string' ? c.question_key.trim() : '';
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({ question_key: key, label: typeof c.label === 'string' ? c.label : '' });
    if (out.length >= MAX_ANSWER_COLUMNS) break;
  }
  return out;
}

const sameColumns = (a: readonly RecruitAnswerColumn[], b: readonly RecruitAnswerColumn[]): boolean =>
  a.length === b.length && a.every((c, i) => c.question_key === b[i].question_key && c.label === b[i].label);

export interface AnswerColumnsSettingsProps {
  /** The study (crm.studies id; every recruit route takes the id, never the slug). */
  studyId: string;
  /** CRM admin: the only people who may change the set. Everyone else sees it disabled. */
  isAdmin: boolean;
  /** The study's recruitment list when it has one (recruiting or paused); null or absent hides the link. */
  sheetHref?: string | null;
}

export default function AnswerColumnsSettings({ studyId, isAdmin, sheetHref = null }: AnswerColumnsSettingsProps) {
  const [available, setAvailable] = useState<RecruitAnswerColumn[]>([]);
  const [saved, setSaved] = useState<RecruitAnswerColumn[]>([]);
  const [draft, setDraft] = useState<RecruitAnswerColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  // A newer load supersedes an older one; closing the page drops whatever is still in flight.
  const genRef = useRef(0);

  const load = useCallback(async () => {
    const gen = ++genRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getRecruitColumns(studyId);
      if (gen !== genRef.current) return;
      const cols = cleanColumns(res.columns);
      setAvailable(cleanQuestions(res.available));
      setSaved(cols);
      setDraft(cols);
      setUnavailable(false);
      setLoaded(true);
    } catch (e) {
      if (gen !== genRef.current) return;
      if (isRecruitUnavailable(e)) setUnavailable(true);
      else setLoadError(recruitErrorMessage(e, "Couldn't load the answer columns. Please try again."));
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, [studyId]);

  useEffect(() => {
    load();
    return () => { genRef.current += 1; };
  }, [load]);

  const questionText = useMemo(() => {
    const m = new Map<string, string>();
    available.forEach((q) => m.set(q.question_key, q.label));
    return m;
  }, [available]);
  const remaining = useMemo(
    () => available.filter((q) => !draft.some((c) => c.question_key === q.question_key)),
    [available, draft],
  );
  const dirty = !sameColumns(draft, saved);
  const canEdit = isAdmin && !saving;
  const full = draft.length >= MAX_ANSWER_COLUMNS;

  const change = (next: (d: RecruitAnswerColumn[]) => RecruitAnswerColumn[]) => {
    setDraft(next);
    setJustSaved(false);
    setSaveError(null);
  };

  const add = (key: string) => {
    const q = available.find((a) => a.question_key === key);
    if (!q) return;
    change((d) => (d.length >= MAX_ANSWER_COLUMNS || d.some((c) => c.question_key === key)
      ? d
      : [...d, { question_key: key, label: defaultColumnLabel(q.label || key) }]));
  };
  const setLabel = (key: string, label: string) =>
    change((d) => d.map((c) => (c.question_key === key ? { ...c, label } : c)));
  const move = (key: string, delta: number) =>
    change((d) => {
      const from = d.findIndex((c) => c.question_key === key);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= d.length) return d;
      const next = d.slice();
      const [col] = next.splice(from, 1);
      next.splice(to, 0, col);
      return next;
    });
  const remove = (key: string) => change((d) => d.filter((c) => c.question_key !== key));
  const discard = () => {
    setDraft(saved);
    setSaveError(null);
  };

  /** The label that is saved: the admin's words, else the question's own (never blank, never too long). */
  const finalLabel = (c: RecruitAnswerColumn): string =>
    squash(c.label).slice(0, ANSWER_LABEL_MAX).trim()
    || defaultColumnLabel(questionText.get(c.question_key) || c.question_key);

  const save = async () => {
    if (!isAdmin || saving || !dirty) return;
    const gen = genRef.current;
    const body = draft.map((c) => ({ question_key: c.question_key, label: finalLabel(c) }));
    setSaving(true);
    setSaveError(null);
    setJustSaved(false);
    try {
      const cols = cleanColumns(await putRecruitColumns(studyId, body));
      if (gen !== genRef.current) return;
      setSaved(cols);
      setDraft(cols);
      setJustSaved(true);
    } catch (e) {
      if (gen !== genRef.current) return;
      setSaveError(recruitErrorMessage(e, "Couldn't save the answer columns. Please try again."));
    } finally {
      if (gen === genRef.current) setSaving(false);
    }
  };

  return (
    <section
      aria-labelledby="recruit-answer-columns-heading"
      className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6"
    >
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 id="recruit-answer-columns-heading" className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <ClipboardCheck className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Recruitment list
        </h2>
        {sheetHref && (
          <Link
            to={sheetHref}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0 sm:py-1"
          >
            Open the recruitment list
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Answer columns: up to six questions from this study&apos;s landing form, shown on the recruitment list under the
        label you give each one. Everyone sees the same set; each recruiter can hide them on the list.
      </p>
      {!isAdmin && (
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">Only a CRM admin can change the answer columns.</p>
      )}

      <div className="mt-3">
        {loading && !loaded ? (
          <div className="flex items-center gap-2 py-3 text-sm text-gray-600 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Loading the answer columns…
          </div>
        ) : unavailable ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The recruitment list isn&apos;t available yet, so its answer columns can&apos;t be set here.
          </p>
        ) : loadError ? (
          <Alert
            tone="error"
            size="sm"
            icon={false}
            actions={<button type="button" onClick={() => { load(); }} className="font-medium underline hover:no-underline">Retry</button>}
          >
            {loadError}
          </Alert>
        ) : (
          <>
            {draft.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isAdmin && available.length > 0 ? 'No answer columns yet. Add a question below.' : 'No answer columns yet.'}
              </p>
            ) : (
              <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                {draft.map((c, i) => {
                  const question = questionText.get(c.question_key);
                  const shown = question || c.question_key;
                  const name = squash(c.label) || shown;
                  return (
                    <li key={c.question_key} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center">
                      <div className="flex min-w-0 flex-1 items-start gap-2">
                        <span className="mt-0.5 w-5 flex-shrink-0 text-xs font-semibold tabular-nums text-gray-400 dark:text-gray-500">
                          {i + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm text-gray-900 dark:text-gray-100" title={shown}>{shown}</p>
                          <p className="truncate font-mono text-[11px] text-gray-400 dark:text-gray-500">{c.question_key}</p>
                          {!question && (
                            <Badge tone="amber" size="sm" className="mt-1" title="Remove it, or put the question back on the landing form">
                              Not on the landing form any more
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 pl-7 sm:flex-shrink-0 sm:pl-0">
                        <input
                          type="text"
                          value={c.label}
                          onChange={(e) => setLabel(c.question_key, e.target.value)}
                          maxLength={ANSWER_LABEL_MAX}
                          disabled={!canEdit}
                          placeholder="Column label"
                          aria-label={`Column label for ${shown}`}
                          className={`${fieldCls} min-h-[44px] w-full min-w-0 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-0 sm:w-56`}
                        />
                        <button
                          type="button"
                          onClick={() => move(c.question_key, -1)}
                          disabled={!canEdit || i === 0}
                          aria-label={`Move ${name} up`}
                          title="Show this column earlier"
                          className={`${iconBtn} flex-shrink-0 disabled:cursor-not-allowed`}
                        >
                          <ArrowUp className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(c.question_key, 1)}
                          disabled={!canEdit || i === draft.length - 1}
                          aria-label={`Move ${name} down`}
                          title="Show this column later"
                          className={`${iconBtn} flex-shrink-0 disabled:cursor-not-allowed`}
                        >
                          <ArrowDown className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(c.question_key)}
                          disabled={!canEdit}
                          aria-label={`Remove ${name}`}
                          title="Remove this column"
                          className={`${iconBtn} flex-shrink-0 hover:text-red-600 disabled:cursor-not-allowed dark:hover:text-red-400`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}

            {available.length === 0 ? (
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                This study has no landing form questions to choose from yet. Publish its landing page form on the Forms
                tab, then pick up to six of its questions here.
              </p>
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) add(e.target.value); }}
                  disabled={!canEdit || full || remaining.length === 0}
                  aria-label="Add a landing form question as a column"
                  className={`${fieldCls} min-h-[44px] w-full max-w-md cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-0 sm:w-auto`}
                >
                  <option value="">
                    {full ? 'Six columns chosen' : remaining.length === 0 ? 'Every question is chosen' : 'Add a question…'}
                  </option>
                  {remaining.map((q) => (
                    <option key={q.question_key} value={q.question_key}>{q.label}</option>
                  ))}
                </select>
                <span className="text-xs tabular-nums text-gray-600 dark:text-gray-400">
                  {draft.length} of {MAX_ANSWER_COLUMNS}
                </span>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => { save(); }}
                disabled={!isAdmin || !dirty || saving}
                className={`${btnPrimary} disabled:cursor-not-allowed`}
              >
                {saving
                  ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  : <Save className="h-4 w-4" aria-hidden="true" />}
                {saving ? 'Saving…' : 'Save columns'}
              </button>
              {isAdmin && dirty && !saving && (
                <button type="button" onClick={discard} className={btnGhost}>
                  Discard changes
                </button>
              )}
              {justSaved && !dirty && (
                <span role="status" className="text-sm text-green-700 dark:text-green-400">
                  Saved. The recruitment list shows these columns from its next refresh.
                </span>
              )}
            </div>
            {saveError && <Alert tone="error" size="sm" className="mt-3">{saveError}</Alert>}
          </>
        )}
      </div>
    </section>
  );
}
